import { ComponentType } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  InputSignal,
  ProviderToken,
  Signal,
  TemplateRef,
  contentChild,
  inject,
  input,
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

interface CustomDialogService<ComponentType, DataType> {
  open: (data: DataType) => MatDialogRef<ComponentType, DataType>;
}

@Component({
  selector: 'mp-dialog-test',
  template: ' <button mat-raised-button (click)="onClick()">Open dialog</button> ',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButton],
})
export class DialogTestComponent<T = unknown, DataType = unknown> {
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly injector: Injector = inject(Injector);

  readonly componentType: InputSignal<ComponentType<T> | undefined> = input<ComponentType<T>>();
  readonly dialogData: InputSignal<DataType> = input<DataType>({} as DataType);
  readonly panelClass: InputSignal<string | undefined> = input<string>();

  readonly customDialogService: InputSignal<ProviderToken<CustomDialogService<T, DataType>> | undefined> =
    input<ProviderToken<CustomDialogService<T, DataType>>>();

  readonly dialogContentTemplate: Signal<TemplateRef<unknown> | undefined> = contentChild<
    TemplateRef<unknown> | undefined
  >(TemplateRef);

  private dialogRef: MatDialogRef<unknown> | undefined;

  onClick(): void {
    const dialogContentTemplate: TemplateRef<unknown> | undefined = this.dialogContentTemplate();
    const customDialogService = this.customDialogService();

    if (customDialogService) {
      const customDialogServiceInstance: CustomDialogService<T, DataType> = this.injector.get(customDialogService);
      this.dialogRef = customDialogServiceInstance.open(this.dialogData());
      return;
    }

    if (dialogContentTemplate) {
      this.dialogRef = this.dialog.open<unknown, DataType>(dialogContentTemplate, {
        data: this.dialogData(),
        panelClass: this.panelClass(),
        injector: this.injector,
      });
      return;
    }

    if (this.componentType()) {
      this.dialogRef = this.dialog.open<T, DataType>(this.componentType() as ComponentType<T>, {
        data: this.dialogData(),
        panelClass: this.panelClass(),
        injector: this.injector,
      });
      return;
    }

    throw new Error('No dialog content provided!');
  }

  closeDialog(): void {
    this.dialogRef?.close();
  }
}
